import { ReactElement, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useStoreActions, useStoreState } from '~/store/hooks';
import {
  compareYearMonth,
  formatMonthParam,
  IYearMonth,
  parseMonthParam,
  shiftYearMonth,
  toYearMonth,
} from '~/utils/date/date';
import { useApiMutation, useApiRequest } from '~/hooks/api';
import {
  ICeArchivedEntry,
  ICeArchivedExemption,
  ICeEntry,
  ICeExemption,
} from '~/services/api/communityEngagement/types';
import Loader from '~/ui/components/common/Loader';

import ArchiveConfirmModal from './components/ArchiveConfirmModal';
import EntriesList from './components/EntriesList';
import EntryModal from './components/EntryModal';
import ExemptionBanner from './components/ExemptionBanner';
import ExemptionModal from './components/ExemptionModal';
import MonthHeader from './components/MonthHeader';
import MonthlyProgress from './components/MonthlyProgress';
import hasThresholds from './helpers/hasThresholds';
import shouldShowProgress from './helpers/shouldShowProgress';

import styles from './CommunityEngagement.module.scss';

const NO_CATEGORIES_TOOLTIP = "Exemption categories haven't been configured for this clinic yet.";

interface ICommunityEngagementProps {
  clinicId: string;
  clientId: string;
}

const CommunityEngagement = ({ clinicId, clientId }: ICommunityEngagementProps): ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams();

  const today = new Date();
  const selected = parseMonthParam(searchParams.get('month'), today);
  const isFutureMonth = compareYearMonth(selected, toYearMonth(today)) > 0;

  const {
    onGetMonthlyRecord,
    onRefreshMonthlyRecord,
    onGetExemptionCategories,
    onGetIsExpansionPlan,
    onArchiveEntry,
    onRestoreEntry,
    onArchiveExemption,
    onRestoreExemption,
    resetCommunityEngagement,
  } = useStoreActions(actions => actions.communityEngagement);
  const { monthlyRecord, exemptionCategories, isExpansionPlan } = useStoreState(
    state => state.communityEngagement,
  );
  // `undefined` = closed, `null` = adding.
  const [editedExemption, setEditedExemption] = useState<ICeExemption | null | undefined>();
  const [editedEntry, setEditedEntry] = useState<ICeEntry | null | undefined>();
  const [entryToArchive, setEntryToArchive] = useState<ICeEntry | null>(null);
  const [exemptionToArchive, setExemptionToArchive] = useState<ICeExemption | null>(null);

  const request = { clinicId, clientId, year: selected.year, month: selected.month };
  const { isLoading } = useApiRequest(({ signal }) => onGetMonthlyRecord({ ...request, signal }), {
    initialLoading: true,
    dependencies: [clinicId, clientId, selected.year, selected.month],
  });
  const { refetch: refreshMonth } = useApiRequest(
    ({ signal }) => onRefreshMonthlyRecord({ ...request, signal }),
    { enabled: false },
  );

  const { isLoading: areCategoriesLoading } = useApiRequest(
    () => onGetExemptionCategories(clinicId),
    { initialLoading: true, dependencies: [clinicId] },
  );

  const { isLoading: isPlanLoading } = useApiRequest(
    () => onGetIsExpansionPlan({ clinicId, clientId }),
    { initialLoading: true, dependencies: [clinicId, clientId] },
  );

  const { mutate: archiveEntry, isLoading: isArchiving } = useApiMutation(
    (entry: ICeEntry) => onArchiveEntry({ ...request, entryId: entry.id }),
    {
      notifyMessage: 'Entry archived',
      throwOnError: false,
      onSuccess: () => {
        setEntryToArchive(null);
        refreshMonth();
      },
    },
  );
  const { mutate: restoreEntry, isLoading: isRestoring } = useApiMutation(
    (entry: ICeArchivedEntry) => onRestoreEntry({ ...request, entryId: entry.id }),
    { notifyMessage: 'Entry restored', throwOnError: false, onSuccess: refreshMonth },
  );
  const { mutate: archiveExemption, isLoading: isArchivingExemption } = useApiMutation(
    (exemption: ICeExemption) => onArchiveExemption({ ...request, exemptionId: exemption.id }),
    {
      notifyMessage: 'Exemption archived',
      throwOnError: false,
      onSuccess: () => {
        setExemptionToArchive(null);
        refreshMonth();
      },
    },
  );
  const { mutate: restoreExemption, isLoading: isRestoringExemption } = useApiMutation(
    (exemption: ICeArchivedExemption) =>
      onRestoreExemption({ ...request, exemptionId: exemption.id }),
    { notifyMessage: 'Exemption restored', throwOnError: false, onSuccess: refreshMonth },
  );

  useEffect(() => () => resetCommunityEngagement(), [resetCommunityEngagement]);

  const goToMonth = (month: IYearMonth) =>
    setSearchParams(
      params => {
        params.set('month', formatMonthParam(month));
        return params;
      },
      { replace: true },
    );

  const entries = monthlyRecord?.entries ?? [];
  const exemptions = monthlyRecord?.exemptions ?? [];
  const archivedExemptions = monthlyRecord?.archivedExemptions ?? [];
  const isExempt = exemptions.length > 0;
  const hasExemptions = isExempt || archivedExemptions.length > 0;
  const showProgress = shouldShowProgress(exemptions, entries);
  const showNotConfigured = !!monthlyRecord && showProgress && !hasThresholds(monthlyRecord);

  const isEligible = !isPlanLoading && isExpansionPlan;
  const hasCategories = !areCategoriesLoading && exemptionCategories.length > 0;
  const lacksCategories = !areCategoriesLoading && !exemptionCategories.length;
  const canAddExemption = isEligible && hasCategories;

  return (
    <div className={styles.wrapper}>
      <MonthHeader
        selected={selected}
        onPrevious={() => goToMonth(shiftYearMonth(selected, -1))}
        onNext={() => goToMonth(shiftYearMonth(selected, 1))}
        showAddActions={!isLoading && !!monthlyRecord}
        showAddExemption={!monthlyRecord?.isFullyCoveredByExemption}
        isAddExemptionDisabled={!canAddExemption}
        addExemptionTooltip={isEligible && lacksCategories ? NO_CATEGORIES_TOOLTIP : ''}
        onAddExemption={() => setEditedExemption(null)}
        isAddEntryDisabled={!isEligible || isFutureMonth}
        onAddEntry={() => setEditedEntry(null)}
      />
      {isLoading && <Loader />}
      {!isLoading && monthlyRecord && (
        <>
          {showProgress && <MonthlyProgress selected={selected} record={monthlyRecord} />}
          {hasExemptions && (
            <ExemptionBanner
              exemptions={exemptions}
              archivedExemptions={archivedExemptions}
              onEditExemption={setEditedExemption}
              onArchiveExemption={setExemptionToArchive}
              onRestoreExemption={restoreExemption}
              isRestoring={isRestoringExemption}
            />
          )}
          <EntriesList
            entries={entries}
            archivedEntries={monthlyRecord.archivedEntries}
            showEmptyState={!entries.length && !showNotConfigured}
            onEditEntry={setEditedEntry}
            onArchiveEntry={setEntryToArchive}
            onRestoreEntry={restoreEntry}
            isRestoring={isRestoring}
          />
        </>
      )}
      {editedExemption !== undefined && (
        <ExemptionModal
          month={request}
          categories={exemptionCategories}
          exemption={editedExemption}
          onClose={() => setEditedExemption(undefined)}
          onSaved={refreshMonth}
        />
      )}
      {editedEntry !== undefined && (
        <EntryModal
          month={request}
          entry={editedEntry}
          onClose={() => setEditedEntry(undefined)}
          onSaved={refreshMonth}
        />
      )}
      {entryToArchive && (
        <ArchiveConfirmModal
          subject="entry"
          isLoading={isArchiving}
          onConfirm={() => archiveEntry(entryToArchive)}
          onClose={() => setEntryToArchive(null)}
        />
      )}
      {exemptionToArchive && (
        <ArchiveConfirmModal
          subject="exemption"
          isLoading={isArchivingExemption}
          onConfirm={() => archiveExemption(exemptionToArchive)}
          onClose={() => setExemptionToArchive(null)}
        />
      )}
    </div>
  );
};

export default CommunityEngagement;
